import { cancel, later } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import { NotificationType } from '@databox/types/domains/user/profile.enums';
import type { UserProfile } from '@databox/types/domains/user/profile.types';

import type { APIData, NpsFeedback, Service } from 'webapp-types';
import { UserRole } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { UserSettingsKey } from 'webapp/enums/user-settings';
import { reload } from 'webapp/utils/common';

import UserPopupForbiddenActionComponent, {
	Action as ForbiddenAction,
} from 'webapp/components/user/popup-forbidden-action';

import type { Type as InfolayerType } from 'webapp/models-custom/infolayer-item/general';
import type { Metadata } from 'webapp/models-custom/user';
import type User from 'webapp/models-custom/user';

// The user record keeps notifications as account-management's UserNotificationSetting bit values.
const NOTIFICATION_BITS: [NotificationType, number][] = [
	[NotificationType.DAILY_SCORECARD, 1],
	[NotificationType.WEEKLY_UPDATES, 2],
	[NotificationType.PUSH_NOTIFICATIONS, 4],
	[NotificationType.SMART_ALERTS, 8],
	[NotificationType.EMAIL_FALLBACK, 16],
	[NotificationType.SLACK, 32],
];

export default class CurrentUserService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('socket') declare socketService: Service.Socket;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('product-updates') declare productUpdatesService: Service.ProductUpdates;

	@tracked model: User | null = null;

	@tracked isPowerUser = false; // if the user is a power user (i.e., if I am a power user)
	@tracked isPlatformAdmin = false; // legacy flag if you have permission for PA
	@tracked isExistingUser = false; // only used when handling demo user, this flags tells us if user is already registered on the app or not
	@tracked isExistingUserEmailSet = false; // only used when handling demo user, if this is true, we can login back into existing user account automatically
	@tracked loggedInAsPowerUser = false; // if you are logged into account as power user and you are impersonating another person
	@tracked metricLibraryExtraInfoToken = '';
	@tracked isOnboarded = false;
	@tracked mobileBoardZoom = 100;
	@tracked allowTransferToSpaceId = false;
	@tracked allowChangingBlockColor = false;
	@tracked rootSpaceId: number | null = null; // warning: session based, might not be real-time accurate
	@tracked rootSpaceLevel: number | null = null; // warning: session based, might not be real-time accurate
	@tracked npsFeedback: NpsFeedback | null = null;
	@tracked isEligibleForNpsFormToBeShown = false;

	// tokens
	@tracked userDataToken: string | null = null;
	@tracked accessToken: string | null = null;
	@tracked accessTokenExpiry: number | null = null; // unix timestamp of expiry

	userGuid = ''; // sub from jwt

	deviceString = ''; // set in setDeviceString

	get id(): string | null {
		return this.model?.id || null;
	}

	get numericId(): number | null {
		return this.model?.numericId || null;
	}

	// Greeting name for React hosts; never a brand name (whitelabeled accounts).
	get displayName(): string {
		return this.model?.firstName || this.model?.email || 'User';
	}

	@tracked _isVerified = false;

	set isVerified(value: boolean) {
		this._isVerified = value;
	}

	get isVerified(): boolean {
		return !!this.model?.isVerified || this._isVerified;
	}

	get isLoggedIn(): boolean {
		return !!this.model?.id;
	}

	get isAdmin(): boolean {
		return !!this.model?.isAdmin;
	}

	get isEditor(): boolean {
		return !!this.model?.isEditor;
	}

	get isUser(): boolean {
		return !!this.model?.isUser;
	}

	get isViewer(): boolean {
		return !!this.model?.isViewer;
	}

	get isDemo() {
		return !!this.model?.isDemo && !this.hasSpecialPermissions;
	}

	get isOwner(): boolean {
		return !!this.model?.isAccountOwner;
	}

	get isEligibleForAccountOwnerOnboarding(): boolean {
		return this.isOwner && !this.accountService.hasPartnerResellerId;
	}

	get isAgencyUserInClientSpace(): boolean {
		return this.accountService.isClient && !!this.model?.isRestricted;
	}

	// Viewers have no Home, and agency users in a client space land on Databoards instead.
	get hasHome(): boolean {
		return !this.isViewer && !this.isAgencyUserInClientSpace;
	}

	get isAgencyUserInPaidClientSpace(): boolean {
		return !!this.model?.isRestricted && this.accountService.hasPartnerResellerId;
	}

	get isClientUserInPaidClientSpace(): boolean {
		return !this.isAgencyUserInPaidClientSpace && this.accountService.hasPartnerResellerId;
	}

	get isAdminInRootSpace(): boolean {
		// can be unreliable, see `rootSpaceLevel`
		return this.rootSpaceLevel === UserRole.ADMIN;
	}

	get hasSpecialPermissions() {
		return this.loggedInAsPowerUser || this.isPowerUser || this.isPlatformAdmin;
	}

	get showBoardTransfer(): boolean {
		return (
			this.allowTransferToSpaceId ||
			(this.accountService.isAgency && this.mmSpacesService.isMultipleSpaces) ||
			this.isAgencyUserInClientSpace
		);
	}

	get isSignedUpFromTemplate(): boolean {
		return !!this.userSettingsService.templateId; // persisent and available after finished onboarding
	}

	get isSlackConnected() {
		return !!this.model?.connectedSlack;
	}

	get isSlackEnabled() {
		return !!this.model?.notifications.includes(32);
	}

	get isEmailEnabled() {
		return !!this.model?.notifications.includes(16);
	}

	get hasDataboxEmail() {
		return !!this.model?.email.includes('@databox.com');
	}

	get hasDataboxSeleniumEmail() {
		return !!this.model?.email.includes('databoxselenium@gmail.com');
	}

	get metadata(): Metadata {
		return (
			this.model?.metadata || {
				title: null,
				department: null,
				role: null,
				seniority: null,
				personaType: null,
				onboardingObjectives: [], // hidden for now, hidden-onboardingObjectives
				sourcesSelection: [],
				onboardingObjectivesSub: null, // hidden for now, hidden-onboardingObjectives
			}
		);
	}

	@cached
	get authTokenAuthorizationHeader(): string {
		const token = this.accessToken;
		if (token) {
			return `Bearer ${token}`;
		} else {
			return '';
		}
	}

	@cached
	get userTokenAuthorizationHeader(): string {
		const token = this.userDataToken;
		if (token) {
			const encodedToken = btoa(`${token}:`);
			return `Basic ${encodedToken}`;
		} else {
			return '';
		}
	}

	get hasSeenBoardSchedulePopup(): boolean {
		return this.userSettingsService.hasSeenBoardSchedulePopup;
	}

	get hasNotSeenNewProductUpdates(): boolean {
		return (
			this.userSettingsService.lastSeenProductUpdates <
			this.productUpdatesService.lastProductUpdate
		);
	}

	handleDemo(): boolean {
		return this.accountService.handleDemo();
	}

	markBoardSchedulePopupSeen(): void {
		if (!this.hasSeenBoardSchedulePopup) {
			void this.userSettingsService.save(UserSettingsKey.BOARD_SCHEDULE_POPUP_SEEN, true);
		}
	}

	setInitialMobileBoardZoom(): void {
		let zoom = 0;

		try {
			// board zoom
			const tmpZoom = localStorage.getItem('currentBoardZoom');
			if (tmpZoom && +tmpZoom > 0) {
				zoom = +tmpZoom;
			}
		} catch (_e) {
			// just ignore, not important enough to warn user
		}

		if (!zoom) {
			const winWidth = window.innerWidth;

			// set initial zoom value based on user's screen size
			if (winWidth < 1500) {
				zoom = 100;
			} else if (winWidth >= 1500 && winWidth < 1800) {
				zoom = 120;
			} else if (winWidth >= 1800 && winWidth < 2200) {
				zoom = 140;
			} else if (winWidth >= 2200) {
				zoom = 160;
			}
		}

		this.updateMobileBoardZoom(zoom);
	}

	updateMobileBoardZoom(zoom: number): void {
		this.mobileBoardZoom = zoom;

		// Safari in Private Browsing Mode supports localStorage but all calls to setItem throw QuotaExceededError
		try {
			localStorage.setItem('currentBoardZoom', String(zoom));
		} catch (_e) {
			// just ignore, not important enough to warn user
		}
	}

	resendVerifyEmail(): Promise<unknown> {
		return this.requestService
			.api('POST', '/auth/resend-verification-token', null, { withoutPrefix: true })
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	openSlackAuthorizationPopup(): void {
		// prep callback
		window.SlackAuth = {
			handleAuthorizationResponse: (message: { text: string; type: InfolayerType }) => {
				this.infolayerService[message.type](message.text);
				window.SlackAuth = null;
			},
		};

		const width = 550,
			height = 530;

		const options = `location=0,status=0,menubar=0,toolbar=0,width=${width},height=${height},scrollbars=yes`;
		const popup = window.open(
			'/slack/authorize?open_in_window',
			'SlackAuthorizeWindow',
			options,
		);

		if (popup) {
			popup.focus();
		}
	}

	activateSlackNotifications(): void {
		const user = this.model!;

		user.notifications = [...user.notifications, 32];
		user.save(true)
			.then(() => {
				this.infolayerService.ok('Slack notifications activated!');
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	chatWithSupport() {
		if (this.handleDemo()) {
			return;
		}

		// The account's support chat: Intercom on a free or trial plan, Genie on a paid one (ai-chat `supportChannel`).
		// The gated Contact support links (`hasSupportChat`) are hidden where there is none; the others do nothing.
		this.aiChatService.openSupportChat();
	}

	openEmailVerificationInfoPopup(text?: string) {
		this.openForbiddenActionPopup(ForbiddenAction.EMAIL, text);
	}

	openForbiddenActionPopup(type: ForbiddenAction, text?: string) {
		const popup = this.popupService.createModal(UserPopupForbiddenActionComponent, {
			popupClass: 'popup-type-forbidden-action',
		});
		popup.open({
			type,
			text,
		});
	}

	private setDeviceString() {
		const rules = [
			['iPhone;', 'phone'],
			['iPad;', 'tablet'],
			['Android.*Mobile Safari', 'phone'], // Chrome/Browser on Android phone
			['Android.*Safari', 'tablet'], // Chrome/Browser on Android tablet
			['.*', 'desktop'], // Fallback to desktop.
		];

		let device = 'desktop';
		const ua = navigator.userAgent;
		for (const rule of rules) {
			const re = new RegExp(rule[0]!);
			if (ua.match(re)) {
				device = rule[1]!;
				break;
			}
		}
		this.deviceString = device;
	}

	// -------------- Metadata START -----------------

	getMetadata(): Promise<APIData.User.MetadataResponse> {
		return this.requestService.api('GET', '/user/metadata');
	}

	updateMetadata(metadata: Metadata): void {
		const model = this.model;

		if (model) {
			model.metadata = metadata;
		}
	}

	// -------------- Metadata END -----------------

	// The Settings dialog's profile, mirrored into the user (saved through ai-bff, so set directly and
	// kept clean). connectedSlack is left out: the webapp's own connect flow is its one writer.
	applyProfile(profile: UserProfile): void {
		if (!this.model || String(profile.id) !== String(this.model.id)) {
			return;
		}

		const notifications = NOTIFICATION_BITS.filter(([type]) => profile.notifications[type]).map(
			([, bit]) => bit,
		);
		const next: Record<string, unknown> = {};

		// Every open of the Profile tab hands the profile over; only what changed is set.
		if (profile.name !== this.model.name) {
			next.name = profile.name;
		}
		if ((profile.avatar ?? '') !== (this.model.avatar ?? '')) {
			next.avatar = profile.avatar ?? '';
		}
		if (profile.timezone !== this.model.timezone) {
			next.timezone = profile.timezone;
		}
		if (notifications.join() !== this.model.notifications.join()) {
			next.notifications = notifications;
		}

		if (Object.keys(next).length) {
			this.model.directPropUpdate(next);
		}
	}

	// -------------- Refreshing tokens START -----------------

	private tokenRefreshTimer?: ReturnType<typeof later>;

	// false while a late refresh (sleep, throttled tab) has left an expired token in place
	get hasValidAccessToken(): boolean {
		const expiry = this.accessTokenExpiry;
		return !!this.accessToken && (!expiry || expiry > Math.round(Date.now() / 1000));
	}

	initRefreshingTokens(): void {
		if (this.accessTokenExpiry) {
			this.scheduleNextTokenRefresh();
		}
	}

	refreshTokensNow(): Promise<void> {
		return this.refreshTokens();
	}

	private scheduleNextTokenRefresh(): void {
		if (!this.accessTokenExpiry) {
			return;
		}
		// refreshing 15s before expiry
		const tsNow = Math.round(Date.now() / 1000),
			nextRefreshIn = this.accessTokenExpiry - tsNow - 15;
		cancel(this.tokenRefreshTimer);
		this.tokenRefreshTimer = later(this, this.refreshTokens, nextRefreshIn * 1000);
	}

	private refreshTokens(): Promise<void> {
		const requestSettings = {
			maxTryCount: 2,
			headers: {
				Authorization: this.authTokenAuthorizationHeader,
				'X-Request-Id': this.requestService.generateRequestId(), // for better debugging
			},
		};
		const promise = this.requestService
			.app<APIData.Auth.RefreshResponse>('POST', '/auth/refresh', null, requestSettings)
			.then(
				(reply) => {
					this.accessToken = reply.accessToken;
					this.accessTokenExpiry = reply.expiry;
					this.scheduleNextTokenRefresh();
					this.socketService.updatePusherAuthToken();
					this.eventManagerService.trigger(AppEvent.ACCESS_TOKEN_REFRESHED);
				},
				(errorData) => {
					// if this is error 401, handle this as expired token (must logout).
					// if this is anything else (e.g. no internet, timeout, ...), just reload the page
					// and maybe this could turn out seamless for the user that is still logged in fusionauth
					if (errorData?.code === 401) {
						const requestId = this.requestService.getRequestIdFromPromise(promise);
						this.requestService.handleExpiredToken(true, {
							t: requestSettings.headers.Authorization,
							requestId,
						});

						throw errorData;
					} else {
						reload();
					}
				},
			)
			.catch(() => {
				// don't show any errors, but clear the tokens
				// case where browser goes to sleep and fails handling the refresh request (even if 200 response)
				this.accessToken = null;
				this.accessTokenExpiry = null;
			});
		return promise;
	}

	// --------------- Refreshing tokens END ------------------

	constructor() {
		// eslint-disable-next-line prefer-rest-params
		super(...arguments);

		this.setInitialMobileBoardZoom();

		this.setDeviceString();
	}
}

declare module '@ember/service' {
	interface Registry {
		'current-user': CurrentUserService;
	}
}
